import {getPublishedAdmissionResults} from '@/helpers/admissionReview'

export function processCVs(CVs) {
  return CVs.map(item => ({
    ...item,
    fullName: item.name,
    code: item.studentCode,
    mathScore: getHighestScore(item, 'math'),
    englishScore: getHighestEnglishScore(item),
    englishScoreNumber: getHighestScore(item, 'english'),
    literatureScore: getHighestScore(item, 'literature'),
    mathAdvanceScore: getHighestScore(item, 'mathAdvance'),
    physicAdvanceScore: getHighestScore(item, 'physicAdvance'),
    englishAdvanceScore: getHighestScore(item, 'englishAdvance'),
    activityScore: getHighestPassed(item, 'activityScore'),
    isExempted: getHighestPassed(item, 'isExempted') === 'Đạt' ? 'Có' : 'Không',
    baseAdmitted: getAdmitted(item, 'baseAdmitted'),
    engAdmitted: getAdmitted(item, 'engAdmitted'),
    mathAdmitted: getAdmitted(item, 'mathAdmitted'),
    preEngAdmitted: getAdmitted(item, 'preEngAdmitted'),
    preMathAdmitted: getAdmitted(item, 'preMathAdmitted'),
    preMath2Admitted: getAdmitted(item, 'preMath2Admitted'),
    studyRecord: getPassEval(item),
    totalScore: getHighestScore(item, 'totalScore'),
    academicBonusScore: getHighestScore(item, 'academicBonusScore'),
    grade10BonusScore: getHighestScore(item, 'grade10BonusScore'),
  }))
}

function getAdmissionResults(item) {
  return getPublishedAdmissionResults(item?.admissionResult)
}

function getResultValue(result, type) {
  return result?.scores?.[type] ?? result?.[type]
}

const admissionProductMatchers = {
  baseAdmitted: product => product?.acronym === 'CTQB6' || product?.name === 'Hệ Chuẩn',
  engAdmitted: product => product?.acronym === 'TA6' || product?.name?.includes('Tiếng Anh'),
}

function getAdmissionStatus(result, type) {
  if (result?.[type] !== undefined && result?.[type] !== null) return result[type]

  const matchesProduct = admissionProductMatchers[type]
  if (!matchesProduct || !Array.isArray(result?.results)) return undefined

  const productResult = result.results.find(item => matchesProduct(item?.product))
  // Schema mới lưu "đạt có điều kiện" bằng status=pass và conditional=true.
  // Quy đổi về passCond để các báo cáo/component dùng processCVs không làm mất trạng thái này.
  if (productResult?.status === 'pass' && productResult?.conditional === true) return 'passCond'
  return productResult?.status
}

function getPassEval(item) {
  const admissionResult = getAdmissionResults(item)
  if (!admissionResult.length) {
    return '-'
  }
  if (admissionResult.map(result => result.passEval).includes(true)) return 'Đạt'
  if (admissionResult.map(result => result.passEval).includes(false)) return 'Không đạt'
  return '-'
}

export function getHighestScore(item, type) {
  const admissionResult = getAdmissionResults(item)
  if (!admissionResult.length) {
    return '-'
  }
  const mathScores = admissionResult
    .map(result => {
      const score = parseFloat(getResultValue(result, type))
      return score
    })
    .filter(score => !isNaN(score))
  const maxScore = mathScores.length > 0 ? Math.max(...mathScores) : '-'
  return isNaN(maxScore) ? '-' : maxScore
}

export function getHighestEnglishScore(item) {
  const admissionResult = getAdmissionResults(item)
  if (!admissionResult.length) {
    return '-'
  }

  const englishResults = admissionResult
    .map(result => {
      const score = getResultValue(result, 'english')
      if (!isNaN(score)) {
        return {
          score: parseFloat(score),
          grade: result?.scores?.englishGrade,
        }
      }
      return undefined
    })
    .filter(result => result !== undefined)

  const validEnglishResults = englishResults.filter(result => {
    return result.score >= -1 && result.score <= 10 && result.score !== 0
  })

  if (validEnglishResults.length === 0) {
    if (englishResults.some(result => result.score === 0)) {
      return '0'
    }
    return '_'
  }

  if (validEnglishResults.length === 1 && validEnglishResults[0].score === -1) {
    return -1
  }

  const highestScore = Math.max(...validEnglishResults.map(result => result.score).filter(score => score !== -1))
  const englishGrade = validEnglishResults.find(result => result.score === highestScore)?.grade
  if (englishGrade) return englishGrade

  if (highestScore >= 9) return 'A'
  if (highestScore >= 7) return 'B'
  if (highestScore >= 5) return 'C'
  if (highestScore >= 2.5) return 'D'
  return 'E'
}

function getHighestPassed(item, type) {
  const admissionResult = getAdmissionResults(item)
  if (!admissionResult.length) return '-'
  const activityResults = admissionResult.map(result => {
    if (type === 'activityScore') return result?.scores?.activity ?? result?.activityScore
    return result?.[type]
  })
  const hasPass = activityResults.some(score => `${score}`.toLowerCase() === 'pass')
  return hasPass ? 'Đạt' : 'Không đạt'
}

function getAdmitted(item, type) {
  const admissionResult = getAdmissionResults(item)
  if (!admissionResult.length) return '-'
  const statuses = admissionResult.map(result => getAdmissionStatus(result, type))

  // Kết quả chính thức được ưu tiên nếu học sinh đã đạt cùng hệ ở một đợt khác.
  if (statuses.some(status => `${status}`.toLowerCase() === 'pass')) return 'Đạt'
  if (statuses.some(status => `${status}`.toLowerCase() === 'passcond')) return 'Đạt có điều kiện'
  return 'Không đạt'
}
